import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Leaf, Mail, Loader2, CheckCircle2 } from "lucide-react";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Impossible d'envoyer le lien. Vérifiez votre connexion.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-4 relative overflow-hidden"
      data-testid="admin-forgot-password-page"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#F5F5F0] via-[#EFEEDF] to-[#E0E5DC]" aria-hidden="true" />
      <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-[#1F4E3D]/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-[#8C6B2F]/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" aria-hidden="true" />

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-11 h-11 rounded-lg bg-[#1F4E3D] flex items-center justify-center text-white shadow-sm">
            <Leaf className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-xl font-medium text-[#1A1C1A]">AgroManage</div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#575A57]">Production & Traçabilité</div>
          </div>
        </div>

        {sent ? (
          <div
            className="bg-white border border-[#E5E5E0] rounded-xl p-7 shadow-[0_8px_30px_rgba(31,78,61,0.08)]"
            data-testid="admin-forgot-sent"
          >
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle2 className="w-6 h-6 text-[#1F4E3D] flex-shrink-0 mt-0.5" />
              <div>
                <h1 className="font-heading text-xl font-medium text-[#1A1C1A]">Email envoyé</h1>
                <p className="text-sm text-[#575A57] mt-1">
                  Si un compte administrateur existe pour cette adresse, un lien
                  de réinitialisation vient d&apos;être envoyé. Vérifiez votre
                  boîte de réception (et vos spams) — le lien expire dans 1&nbsp;heure.
                </p>
              </div>
            </div>
            <div className="text-xs text-[#575A57] bg-[#F5F5F0] rounded-md px-3 py-2 mb-6">
              Vous ne recevez rien après 5 minutes ? Vérifiez l&apos;adresse
              saisie — elle doit correspondre à votre compte administrateur
              (différent d&apos;un compte boutique).
            </div>
            <Link
              to="/login"
              className="block text-center text-sm underline text-[#1F4E3D]"
              data-testid="admin-forgot-back-login"
            >
              ← Retour à la connexion
            </Link>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="bg-white border border-[#E5E5E0] rounded-xl p-7 shadow-[0_8px_30px_rgba(31,78,61,0.08)]"
            data-testid="admin-forgot-form"
          >
            <h1 className="font-heading text-2xl font-medium text-[#1A1C1A]">Mot de passe oublié</h1>
            <p className="text-sm text-[#575A57] mt-1 mb-6">
              Entrez l&apos;adresse email de votre compte administrateur — nous
              vous envoyons un lien pour définir un nouveau mot de passe.
            </p>

            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="mb-1.5 block text-[#1A1C1A]">Adresse email</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#575A57] pointer-events-none" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@maison-thai.pro"
                    required
                    className="pl-9"
                    data-testid="admin-forgot-email"
                  />
                </div>
              </div>

              {error && (
                <div
                  className="bg-[#FCEEEA] border border-[#C05C47]/30 text-[#8B3D2D] text-sm rounded-md px-3 py-2"
                  data-testid="admin-forgot-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || !email}
                className="w-full bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
                data-testid="admin-forgot-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours…</>
                ) : (
                  "Envoyer le lien"
                )}
              </Button>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm underline text-[#575A57] hover:text-[#1F4E3D]"
                data-testid="admin-forgot-back-login"
              >
                ← Retour à la connexion
              </Link>
            </div>
          </form>
        )}

        <p className="text-center text-xs text-[#575A57] mt-6">
          Espace privé — accès réservé à l&apos;équipe Maison Thaï.
        </p>
      </div>
    </div>
  );
}
